// features/workstations/qqq/prediction/roadmap-sidebar.js
// ═══════════════════════════════════════════════════════════
// Collapsible right-side panel inside the Prediction tab that
// renders docs/QQQ_PREDICTION_ROADMAP.md.
//
// Lightweight markdown renderer (no extra deps). Handles headers,
// bullets, tables, code-fences. Good enough for the roadmap.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

// Minimal markdown → HTML for our roadmap format.
// Not a full parser; covers what the roadmap uses.
function renderMarkdown(md) {
  if (!md) return '';
  const lines = md.split('\n');
  let html = '';
  let inCode = false;
  let inList = false;
  let inTable = false;
  let tableHeader = null;

  const closeOpenBlocks = () => {
    if (inList) { html += '</ul>'; inList = false; }
    if (inTable) { html += '</tbody></table>'; inTable = false; tableHeader = null; }
  };

  const inlineFmt = (s) => {
    return s
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code fence
    if (line.startsWith('```')) {
      closeOpenBlocks();
      if (!inCode) { html += '<pre><code>'; inCode = true; }
      else { html += '</code></pre>'; inCode = false; }
      continue;
    }
    if (inCode) {
      html += line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') + '\n';
      continue;
    }

    // Headers
    if (line.startsWith('# ')) { closeOpenBlocks(); html += `<h1>${inlineFmt(line.slice(2))}</h1>`; continue; }
    if (line.startsWith('## ')) { closeOpenBlocks(); html += `<h2>${inlineFmt(line.slice(3))}</h2>`; continue; }
    if (line.startsWith('### ')) { closeOpenBlocks(); html += `<h3>${inlineFmt(line.slice(4))}</h3>`; continue; }
    if (line.startsWith('#### ')) { closeOpenBlocks(); html += `<h4>${inlineFmt(line.slice(5))}</h4>`; continue; }

    // Horizontal rule
    if (line.trim() === '---') { closeOpenBlocks(); html += '<hr/>'; continue; }

    // Table detection (| col | col |)
    if (line.startsWith('|') && line.endsWith('|')) {
      const cells = line.split('|').slice(1, -1).map((c) => c.trim());
      const nextLine = lines[i + 1] || '';
      if (!inTable && nextLine.match(/^\|[\s\-:]+\|/)) {
        // Header row
        closeOpenBlocks();
        html += '<table><thead><tr>';
        cells.forEach((c) => html += `<th>${inlineFmt(c)}</th>`);
        html += '</tr></thead><tbody>';
        inTable = true;
        tableHeader = cells;
        i++; // skip separator line
        continue;
      }
      if (inTable) {
        html += '<tr>';
        cells.forEach((c) => html += `<td>${inlineFmt(c)}</td>`);
        html += '</tr>';
        continue;
      }
    } else if (inTable) {
      html += '</tbody></table>'; inTable = false; tableHeader = null;
    }

    // Lists
    if (line.match(/^\s*[-*]\s+/)) {
      if (!inList) { html += '<ul>'; inList = true; }
      html += `<li>${inlineFmt(line.replace(/^\s*[-*]\s+/, ''))}</li>`;
      continue;
    }
    if (line.match(/^\s*\d+\.\s+/)) {
      if (!inList) { html += '<ol>'; inList = true; }
      html += `<li>${inlineFmt(line.replace(/^\s*\d+\.\s+/, ''))}</li>`;
      continue;
    }
    if (inList && line.trim() === '') {
      html += '</ul>'; inList = false;
    }

    // Paragraph
    if (line.trim() === '') { html += ''; continue; }
    html += `<p>${inlineFmt(line)}</p>`;
  }
  closeOpenBlocks();
  if (inCode) html += '</code></pre>';

  return html;
}

export default function RoadmapSidebar() {
  const [open, setOpen] = useState(false);
  const [md, setMd] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (md) return;
    setLoading(true);
    try {
      const res = await fetch('/api/qqq/roadmap-doc');
      const text = await res.text();
      setMd(text);
    } catch (e) {
      setMd('# Error loading roadmap\n\n' + e.message);
    } finally {
      setLoading(false);
    }
  }, [md]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <>
      <button
        className="qqq-roadmap-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Toggle improvement roadmap"
      >
        {open ? '✕ Close roadmap' : '📖 Improvement roadmap'}
      </button>

      {open && (
        <aside className="qqq-roadmap-sidebar">
          <div className="qqq-roadmap-sidebar__header">
            <h3>Improvement Roadmap</h3>
            <button onClick={() => setOpen(false)} className="qqq-roadmap-sidebar__close">✕</button>
          </div>
          <div className="qqq-roadmap-sidebar__body">
            {loading && <p style={{ opacity: 0.6 }}>Loading…</p>}
            {md && (
              <div
                className="qqq-roadmap-md"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(md) }}
              />
            )}
          </div>
        </aside>
      )}

      <style jsx>{`
        .qqq-roadmap-toggle {
          position: fixed;
          right: 16px;
          top: 120px;
          z-index: 100;
          background: #111118;
          color: #c8a96e;
          border: 1px solid #c8a96e66;
          padding: 8px 14px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          font-family: -apple-system, sans-serif;
        }
        .qqq-roadmap-toggle:hover {
          background: #c8a96e11;
        }
        .qqq-roadmap-sidebar {
          position: fixed;
          right: 0;
          top: 0;
          bottom: 0;
          width: 480px;
          max-width: 50vw;
          background: #07070a;
          border-left: 1px solid #272736;
          z-index: 99;
          display: flex;
          flex-direction: column;
          box-shadow: -8px 0 32px rgba(0,0,0,0.5);
        }
        .qqq-roadmap-sidebar__header {
          padding: 16px 20px;
          border-bottom: 1px solid #272736;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }
        .qqq-roadmap-sidebar__header h3 {
          margin: 0;
          font-size: 14px;
          color: #c8a96e;
          font-weight: 600;
        }
        .qqq-roadmap-sidebar__close {
          background: transparent;
          border: none;
          color: #888;
          font-size: 16px;
          cursor: pointer;
        }
        .qqq-roadmap-sidebar__close:hover { color: #c8a96e; }
        .qqq-roadmap-sidebar__body {
          overflow-y: auto;
          padding: 20px;
          color: #ccc;
          font-size: 13px;
          line-height: 1.55;
        }
        :global(.qqq-roadmap-md h1) { color: #c8a96e; font-size: 18px; margin: 0 0 12px; border-bottom: 1px solid #272736; padding-bottom: 8px; }
        :global(.qqq-roadmap-md h2) { color: #c8a96e; font-size: 15px; margin: 20px 0 10px; }
        :global(.qqq-roadmap-md h3) { color: #b8956a; font-size: 13px; margin: 14px 0 6px; }
        :global(.qqq-roadmap-md h4) { color: #aaa; font-size: 12px; margin: 10px 0 4px; }
        :global(.qqq-roadmap-md p) { margin: 6px 0; color: #aaa; }
        :global(.qqq-roadmap-md ul), :global(.qqq-roadmap-md ol) { padding-left: 18px; margin: 6px 0; color: #aaa; }
        :global(.qqq-roadmap-md li) { margin: 2px 0; }
        :global(.qqq-roadmap-md code) { background: #171723; color: #b8956a; padding: 1px 4px; border-radius: 3px; font-size: 11px; }
        :global(.qqq-roadmap-md pre) { background: #0c0c11; border: 1px solid #1e1e2a; padding: 10px; border-radius: 4px; overflow-x: auto; margin: 8px 0; }
        :global(.qqq-roadmap-md pre code) { background: transparent; color: #ccc; padding: 0; font-size: 11px; line-height: 1.4; }
        :global(.qqq-roadmap-md strong) { color: #c8a96e; }
        :global(.qqq-roadmap-md table) { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 11px; }
        :global(.qqq-roadmap-md th) { text-align: left; padding: 6px 8px; background: #111118; color: #c8a96e; border: 1px solid #272736; font-weight: 600; }
        :global(.qqq-roadmap-md td) { padding: 6px 8px; border: 1px solid #1e1e2a; color: #aaa; vertical-align: top; }
        :global(.qqq-roadmap-md hr) { border: none; border-top: 1px solid #272736; margin: 16px 0; }
        :global(.qqq-roadmap-md a) { color: #c8a96e; text-decoration: none; }
        :global(.qqq-roadmap-md a:hover) { text-decoration: underline; }
      `}</style>
    </>
  );
}
